import React, { useState } from 'react';
import { Form, Input, Radio, Switch, Card } from 'antd';
import { EditOutlined, FormOutlined } from '@ant-design/icons';
import { GridLayout } from '../GridLayout';
import { SectionHeader } from '../SectionHeader';
import { SubsectionHeader } from '../SubsectionHeader';
import { SectionDivider } from '../SectionDivider';
import { CompanyAutocomplete, Company } from '../../common/CompanyAutocomplete';
import { CompanyModal } from '../../common/CompanyModal';
import { Typography } from '../../design-system/typography';
import { colors, spacing } from '../../design-system/tokens';

interface ContractReferenceOption {
  value: string;
  year: string;
  label: string;
}

interface ContractPartiesFormProps {
  onSupplierChange?: (supplier: Company) => void;
  contractReferenceOptions?: ContractReferenceOption[];
  selectedReference?: string;
  onReferenceChange?: (reference: string) => void;
}

export const ContractPartiesForm: React.FC<ContractPartiesFormProps> = ({ 
  onSupplierChange, 
  contractReferenceOptions = [], 
  selectedReference, 
  onReferenceChange
}) => {
  const [companyModalVisible, setCompanyModalVisible] = useState(false);
  const [modalCompanyType, setModalCompanyType] = useState<'supplier' | 'buyer' | 'seller'>('supplier');
  const [importMode, setImportMode] = useState<'manual' | 'email'>('manual');
  const [emailText, setEmailText] = useState('');

  const handleCreateNewCompany = (companyType: 'supplier' | 'buyer' | 'seller') => {
    setModalCompanyType(companyType);
    setCompanyModalVisible(true);
  };

  const handleCompanyModalSave = (company: Company) => {
    // In real implementation, this would update the backend and refresh the autocomplete options
    console.log('Saved company:', company);
    setCompanyModalVisible(false);
  };

  const handleSupplierSelect = (value: string, company?: Company) => {
    if (company && onSupplierChange) {
      onSupplierChange(company);
    }
  };

  const handleImportModeToggle = (checked: boolean) => {
    setImportMode(checked ? 'email' : 'manual');
  };

  const handleEmailTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEmailText(e.target.value);
    // In the future, this will parse the email with AI and populate all form fields
    // For now, we'll keep the text separate from the manual form data
  };

  return (
    <div>
      {/* Contract Information */}
      <SectionHeader title="Contract Information" />
      <GridLayout columns="1fr 1fr 1fr" style={{ marginTop: spacing.lg }}>
        <Form.Item
          name="contractDate"
          label="Contract Date"
          rules={[{ required: true, message: 'Contract date is required' }]}
        >
          <Input 
            placeholder="e.g., 30-Jul-25"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="rimtradeRef"
          label="Contract Reference & Crop Year"
          rules={[{ required: true, message: 'Contract reference is required' }]}
        >
          {contractReferenceOptions.length > 0 ? (
            <Radio.Group 
              value={selectedReference}
              onChange={(e) => onReferenceChange?.(e.target.value)}
              style={{ width: '100%' }}
            >
              {contractReferenceOptions.map(option => (
                <Radio.Button 
                  key={option.value} 
                  value={option.value}
                  style={{ marginBottom: '4px' }}
                >
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{option.value}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{option.label}</div>
                  </div>
                </Radio.Button>
              ))}
            </Radio.Group>
          ) : (
            <Input 
              placeholder="Select supplier first"
              disabled
              style={{ width: '100%' }}
            />
          )}
        </Form.Item>

        <Form.Item
          name="brand"
          label="Brand"
        >
          <Input placeholder="Brand name (if applicable)" />
        </Form.Item>
      </GridLayout>

      <SectionDivider margin="xl" />

      {/* Party Information */}
      <SectionHeader title="Party Information" />
      
      <SubsectionHeader title="Supplier" />
      <GridLayout columns="1fr" style={{ marginTop: spacing.lg }}>
        <Form.Item
          name="supplier"
          label="Supplier Company"
          rules={[{ required: true, message: 'Supplier is required' }]}
        >
          <CompanyAutocomplete
            placeholder="Search for supplier..."
            companyType="supplier"
            onCreateNew={() => handleCreateNewCompany('supplier')}
            onChange={handleSupplierSelect}
          />
        </Form.Item>
      </GridLayout>

      <SectionDivider margin="lg" />

      <SubsectionHeader title="Seller" />
      <GridLayout columns="1fr" style={{ marginTop: spacing.lg }}>
        <Form.Item
          name="seller"
          label="Seller Company"
          tooltip="Leave blank if Rimtrade is not acting as the seller"
        >
          <CompanyAutocomplete
            placeholder="Search for seller (optional)..."
            companyType="seller"
            onCreateNew={() => handleCreateNewCompany('seller')}
          />
        </Form.Item>
      </GridLayout>

      <SectionDivider margin="lg" />

      <SubsectionHeader title="Buyer" />
      <GridLayout columns="1fr" style={{ marginTop: spacing.lg }}>
        <Form.Item
          name="buyer"
          label="Buyer Company"
          rules={[{ required: true, message: 'Buyer is required' }]}
        >
          <CompanyAutocomplete
            placeholder="Search for buyer..."
            companyType="buyer"
            onCreateNew={() => handleCreateNewCompany('buyer')}
          />
        </Form.Item>
      </GridLayout>

      <SectionDivider margin="xl" />

      {/* Contract Email Import Option */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: spacing.md
      }}>
        <div>
          <SubsectionHeader title="Alternative: Import from Contract Email" />
          <Typography 
            variant="valueAddress" 
            style={{ 
              color: colors.text.secondary, 
              marginTop: spacing.xs,
              display: 'block'
            }}
          >
            Skip manual entry by pasting a complete contract email. AI will parse and populate all contract details, terms, and shipments across all steps.
          </Typography>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
          <Typography variant="label" style={{ color: colors.text.secondary }}>
            Manual
          </Typography>
          <Switch
            checked={importMode === 'email'}
            onChange={handleImportModeToggle}
          />
          <Typography variant="label" style={{ color: colors.text.secondary }}>
            Email Import
          </Typography>
        </div>
      </div>

      {importMode === 'email' && (
        <>
          <Typography 
            variant="valueAddress" 
            style={{ 
              color: colors.text.secondary, 
              marginBottom: spacing.md,
              display: 'block'
            }}
          >
            <strong>Paste your contract email below:</strong><br/>
            <code style={{ 
              display: 'block', 
              backgroundColor: colors.background.tertiary, 
              padding: spacing.sm, 
              marginTop: spacing.xs,
              borderRadius: '4px',
              fontSize: '12px'
            }}>
              Example: "1x40ft-FCL(880 cases) California Nut Butte/Padre Extra #1 J-spec 32/34, shipment Nov '25. $2.60 FAS"
            </code>
          </Typography>

          <Card>
            <Input.TextArea
              value={emailText}
              onChange={handleEmailTextChange}
              placeholder="Paste complete contract email here... (AI parsing will populate all form fields in steps 1-3)"
              rows={10}
              style={{ 
                fontSize: '14px',
                fontFamily: 'monospace'
              }}
            />
          </Card>
        </>
      )}

      {/* Company Creation Modal - Available in both modes */}
      <CompanyModal
        visible={companyModalVisible}
        onCancel={() => setCompanyModalVisible(false)}
        onSave={handleCompanyModalSave}
        companyType={modalCompanyType}
      />
    </div>
  );
};